import * as THREE from 'three';
import type { PlayerData, Vector3 } from 'shared';

export class RemotePlayer {
  public mesh: THREE.Group;
  public playerId: string;
  public playerName: string;

  private body: THREE.Mesh;
  private nameplate: THREE.Sprite;

  // Interpolation
  private targetPosition: THREE.Vector3;
  private targetRotation: THREE.Euler;
  private interpolationSpeed: number = 10;

  constructor(scene: THREE.Scene, playerData: PlayerData) {
    this.playerId = playerData.id;
    this.playerName = playerData.name;

    this.mesh = new THREE.Group();

    // Create player body (similar to local player but different color)
    const bodyGeometry = new THREE.CapsuleGeometry(0.4, 1.2, 4, 8);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: this.getPlayerColor(playerData.class),
      roughness: 0.7,
      metalness: 0.2,
    });
    this.body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.body.position.y = 1.1;
    this.body.castShadow = true;
    this.mesh.add(this.body);

    // Head
    const headGeometry = new THREE.SphereGeometry(0.35, 12, 8);
    const headMaterial = new THREE.MeshStandardMaterial({
      color: 0xffdbac,
      roughness: 0.8,
    });
    const head = new THREE.Mesh(headGeometry, headMaterial);
    head.position.y = 2.1;
    head.castShadow = true;
    this.mesh.add(head);

    // Nameplate
    this.nameplate = this.createNameplate();
    this.mesh.add(this.nameplate);

    // Set initial position
    this.mesh.position.set(
      playerData.position.x,
      playerData.position.y,
      playerData.position.z
    );
    this.targetPosition = this.mesh.position.clone();
    this.targetRotation = new THREE.Euler(
      playerData.rotation.x,
      playerData.rotation.y,
      playerData.rotation.z
    );

    scene.add(this.mesh);
  }

  private getPlayerColor(playerClass: string): number {
    switch (playerClass) {
      case 'warrior':
        return 0xcc4444; // Red
      case 'mage':
        return 0x4444cc; // Blue
      case 'archer':
        return 0x44cc44; // Green
      default:
        return 0x888888; // Gray
    }
  }

  private createNameplate(): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 16, 256, 32);

    // Text
    ctx.fillStyle = '#00ff00';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.playerName, 128, 32);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
    });
    const sprite = new THREE.Sprite(material);
    sprite.position.y = 2.8;
    sprite.scale.set(2, 0.5, 1);

    return sprite;
  }

  public updatePosition(position: Vector3, rotation: Vector3): void {
    this.targetPosition.set(position.x, position.y, position.z);
    this.targetRotation.set(rotation.x, rotation.y, rotation.z);
  }

  public update(deltaTime: number): void {
    // Smooth interpolation to target position
    this.mesh.position.lerp(this.targetPosition, this.interpolationSpeed * deltaTime);

    // Smooth rotation interpolation
    const currentY = this.mesh.rotation.y;
    const targetY = this.targetRotation.y;

    // Handle angle wrapping
    let diff = targetY - currentY;
    if (diff > Math.PI) diff -= Math.PI * 2;
    if (diff < -Math.PI) diff += Math.PI * 2;

    this.mesh.rotation.y += diff * this.interpolationSpeed * deltaTime;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public destroy(): void {
    this.mesh.parent?.remove(this.mesh);
  }
}
